import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { BehaviorSubject, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FormMode } from 'src/app/controls/common/form-mode';
import { BoxAccessibilityChangeEvent } from 'src/app/models/box-accessibility-change-event';
import { IBoxAccessibility } from 'src/app/models/interfaces/box-accessibility.interface';
import { IParamsValues } from 'src/app/models/interfaces/params-values.interface';
import { IUser } from 'src/app/models/interfaces/user.interface';
import { IViewAccessibility } from 'src/app/models/interfaces/view-accessibility.interface';
import { ViewAccessibilityChangeEvent } from 'src/app/models/view-accessibility-change-event';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { ParametersValuesFormComponent } from '../../parameters/parameters-values-form/parameters-values-form.component';
import { GroupParentFormComponent } from '../group-parent-form/group-parent-form.component';
import { UserDetailContext } from '../user-detail-context';
import { UserFormComponent } from '../user-form/user-form.component';
import { UserViewsAccessibilityComponent } from '../user-views-accessibility/user-views-accessibility.component';

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styleUrls: ['./user-detail.component.scss'],
    imports: [MatTabsModule, UserFormComponent, UserViewsAccessibilityComponent, ParametersValuesFormComponent, GroupParentFormComponent],
})
export class UserDetailComponent implements OnInit {
    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Reference au composant groupe d'onglets.
     */
    @ViewChild(MatTabGroup)
    tabGroup: MatTabGroup;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public userUpdated: EventEmitter<IUser> = new EventEmitter();

    /**
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public paramsValuesUpdated: EventEmitter<IParamsValues> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public accessibilitiesUpdated: EventEmitter<BoxAccessibilityChangeEvent> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    @Output()
    public viewsAccessibilitiesUpdated: EventEmitter<ViewAccessibilityChangeEvent> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    /**
     * Contexte du formulaire.
     */
    public _context: UserDetailContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<UserDetailContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: UserDetailContext) {
        // set the latest value for _data BehaviorSubject
        this._context$.next(value);
    }

    constructor(private paramsValuesService: ParamsValuesService) {}

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        /**
         * S'abonne aux changements de contexte
         */
        this._context$.subscribe((x) => {
            this._context = x;
        });
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont sauvegardés.
     * @param user: Infos du user modifié.
     */
    public onUserUpdated(user: IUser) {
        this.userUpdated.emit(user);
    }

    public onParamsValuesUpdated(paramsvalues: IParamsValues) {
        this.paramsValuesUpdated.emit(paramsvalues);
    }

    public onAccessibilitiesUpdated(boxesAccessibility: IBoxAccessibility[]) {
        this.accessibilitiesUpdated.emit(new BoxAccessibilityChangeEvent(this._context.editedUserId, boxesAccessibility));
    }

    public onViewsAccessibilitiesUpdated(viewsAccessibility: IViewAccessibility[]) {
        this.viewsAccessibilitiesUpdated.emit(new ViewAccessibilityChangeEvent(this._context.editedUserId, viewsAccessibility));
    }

    public onParamsExported() {
        this.paramsValuesService
            .exportForUser(this._context.editedUserId)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Paramètres de l\`utilisateur ${this._context.editedUserId}.xlsx`;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    /**
     * Appellé lorsque des changements sur le formulaire "infos" sont annulés.
     * @param user: Infos de l'article modifié.
     */
    public onCanceled(value: boolean) {
        this.canceled.emit(value);
    }
}
